'use client';

import type {
  calculateSavingsPerBudgetCategory,
  simulateProjections,
} from '~/landing/app/(garance)/cagnup/calculations';
import type { FormSchema } from '~/landing/app/(garance)/cagnup/schema';
import type { AnnualRate } from '~/landing/core/simulator/future-savings-planner';

import {
  Card,
  CardContent,
  FieldWrapper,
  FormField,
  ProjectionChart,
  Separator,
  TextInput,
  Typography,
} from '@polyseme/design-system';
import {
  formatCurrency,
  formatPercent,
} from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import {
  MAX_BUDGET,
  budgetCategories,
} from '~/landing/app/(garance)/cagnup/constants';
import { Helper } from '~/landing/components';
import { getRiskProfileByRates } from '~/landing/core/simulator/future-savings-planner/utils';

type SimulationFormProps = {
  projections: ReturnType<typeof simulateProjections>;
  rate: AnnualRate;
  savingsPerCategory: ReturnType<typeof calculateSavingsPerBudgetCategory>;
};

const VARIANT = 'cashback';

const COLORS = {
  contribution: {
    textColor: 'text-green-500',
    dotBg: 'bg-green-500',
    lineColor: '#22c55e',
  },
  yield: {
    dotBg: 'bg-accent',
    textColor: 'text-accent',
    lineColor: '#B6DCE3',
  },
  projection: {
    dotBg: 'bg-primary',
    textColor: 'text-primary',
    lineColor: '#006F73',
  },
};

export const SimulationForm = ({
  projections,
  rate,
  savingsPerCategory,
}: SimulationFormProps) => {
  const t = useTranslations('landing');
  const form = useFormContext<FormSchema>();
  const riskProfile = getRiskProfileByRates(rate);

  if (!riskProfile)
    throw new Error('Risk profile not found for the given rates');

  return (
    <>
      <header className="space-y-1">
        <Typography variant="title-4">
          {t('cagnup.simulation.title')}
        </Typography>
        <Typography className="text-muted-foreground" variant="body">
          {t('cagnup.simulation.subtitle')}
        </Typography>
      </header>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardContent className="space-y-6 p-6">
            <Typography variant="title-4">
              {t('cagnup.simulation.form.title')}
            </Typography>
            <div className="grid gap-6 md:grid-cols-2">
              {budgetCategories.map((category) => (
                <Card key={category.id}>
                  <CardContent className="flex h-full flex-col gap-2.5 p-3">
                    <Typography variant="title-5">
                      {t(`cagnup.simulation.form.${category.id}.label`)}
                    </Typography>
                    <FormField
                      control={form.control}
                      name={`budget.${category.id}`}
                      render={({ field }) => (
                        <FieldWrapper
                          description={t(
                            'cagnup.simulation.form.monthlyBudget'
                          )}
                        >
                          <TextInput
                            {...field}
                            aria-label={`${t(`cagnup.simulation.form.${category.id}.label`)} ${t('cagnup.simulation.form.monthlyBudget')}`}
                            inputMode="decimal"
                            rightIcon="euro"
                            type="number"
                            onChange={(event) => {
                              const inputValue = parseFloat(event.target.value);
                              const value = Math.min(
                                MAX_BUDGET,
                                Math.max(0, inputValue)
                              );
                              field.onChange(value);
                            }}
                          />
                        </FieldWrapper>
                      )}
                    />
                    <Typography className="text-primary">
                      {t('cagnup.simulation.partners')}
                    </Typography>
                    <ul className="space-y-1">
                      {category.stores.map((store) => (
                        <li
                          key={store.name}
                          className="flex justify-between gap-2"
                        >
                          <span>{store.name}</span>
                          <span>
                            {formatPercent(store.cashback.percent, {
                              digits: 2,
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto space-y-2.5">
                      <Separator />
                      <div className="flex flex-wrap justify-between gap-2 rounded bg-[#E9F5F7] px-1 py-0.5">
                        <span className="md:whitespace-nowrap">
                          {t('cagnup.simulation.estimatedSavings')}
                        </span>
                        <span>
                          {formatCurrency(savingsPerCategory[category.id], {
                            digits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-6 p-6">
            <Typography variant="title-4">
              {t('cagnup.simulation.graph.title')}
            </Typography>

            <ProjectionChart
              collapsible
              chartConfig={{
                median: {
                  color: COLORS.projection.lineColor,
                },
                investmentMade: {
                  color: COLORS.contribution.lineColor,
                },
                optimistic: {
                  color: COLORS.yield.lineColor,
                },
                pessimistic: {
                  color: COLORS.yield.lineColor,
                },
              }}
              config={{
                contribution: {
                  colors: COLORS.contribution,
                  legend: t(
                    `retraite.futureSavingsPlanner.result.caption.contribution.${VARIANT}`
                  ),
                  explanation: {
                    subtitle: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.projection.label'
                    ),
                    sentence: t('cagnup.simulation.graph.caption.contribution'),
                  },
                },
                yield: {
                  colors: COLORS.yield,
                  legend: t(
                    `retraite.futureSavingsPlanner.result.caption.yield.${VARIANT}`
                  ),
                  explanation: {
                    subtitle: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.yield.label'
                    ),
                    sentence: t('cagnup.simulation.graph.caption.yield', {
                      riskProfile: t(
                        `retraite.riskProfile.${riskProfile}.name`
                      ),
                      minPercentRate: formatPercent(rate.min),
                      maxPercentRate: formatPercent(rate.max),
                    }),
                  },
                },
                projection: {
                  colors: COLORS.projection,
                  legend: t(
                    `retraite.futureSavingsPlanner.result.caption.projection.${VARIANT}`
                  ),
                  explanation: {
                    subtitle: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.projection.label'
                    ),
                    sentence: t('cagnup.simulation.graph.caption.projection', {
                      medianPercentRate: formatPercent(rate.median),
                    }),
                  },
                },
              }}
              explanationsTitle={t(
                'retraite.futureSavingsPlanner.result.explanation.GERMEN.label'
              )}
              maxAmount={0}
              projections={projections}
              tooltipLabels={{
                investmentMade: t(
                  `retraite.futureSavingsPlanner.result.tooltip.investmentMade.${VARIANT}`
                ),
                interestGenerated: t(
                  'retraite.futureSavingsPlanner.result.tooltip.interestGenerated'
                ),
                totalSavings: t(
                  `retraite.futureSavingsPlanner.result.tooltip.savings.atYear`
                ),
                totalSavingsAtLastYear: t(
                  `retraite.futureSavingsPlanner.result.tooltip.savings.atLastYear.${VARIANT}`
                ),
              }}
            />
          </CardContent>
        </Card>
      </div>

      <Helper>
        {t('cagnup.simulation.information', {
          date: new Date('08/01/2025').toLocaleDateString('fr-FR'),
          medianPercentRate: formatPercent(rate.median),
          riskProfile: t(`retraite.riskProfile.${riskProfile}.name`),
        })}
      </Helper>
    </>
  );
};
